import React, { useEffect, useState, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { apiFetch } from '../../api/apiClient';
import { CRYPTO_UNIVERSE as FALLBACK_CRYPTO } from '../../features/marketdata/stockUniverse';

/**
 * Crypto workspace layout — fetches CoinGecko top-200 universe + batch quotes once,
 * passes { universe, quotes, loading, refresh } via Outlet context.
 */
export default function CryptoWorkspace() {
  const [universe, setUniverse] = useState(FALLBACK_CRYPTO);
  const [quotes,   setQuotes]   = useState({});
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/crypto/top?limit=200');
        if (!res.ok) return;
        const list = await res.json();
        if (!cancelled && Array.isArray(list) && list.length > 0) setUniverse(list);
      } catch { /* keep fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchQuotes = useCallback(async () => {
    if (!universe.length) return;
    setLoading(true);
    try {
      const chunks = [];
      for (let i = 0; i < universe.length; i += 200)
        chunks.push(universe.slice(i, i + 200).map(x => x.ticker).join(','));
      const responses = await Promise.allSettled(
        chunks.map(t => apiFetch(`/api/stock/quotes?tickers=${encodeURIComponent(t)}`).then(r => r.json()))
      );
      const merged = {};
      responses.forEach(r => { if (r.status === 'fulfilled' && r.value) Object.assign(merged, r.value); });
      setQuotes(merged);
    } catch { setQuotes({}); }
    finally { setLoading(false); }
  }, [universe]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  useEffect(() => {
    const id = setInterval(fetchQuotes, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchQuotes]);

  return <Outlet context={{ universe, quotes, loading, refresh: fetchQuotes }} />;
}
